import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ShieldCheck, X } from 'lucide-react';
import {
  CONSENT_SETTINGS_EVENT,
  disableOptionalTracking,
  enableOptionalTracking,
  ensureGoogleTag,
  getStoredConsent,
  isConsentUiHiddenPath,
  isPrivateAnalyticsPath,
  storeConsent,
} from '@/lib/analyticsConsent';
import { getAggregateStatisticsPreference, setAggregateStatisticsPreference } from '@/lib/firstPartyFunnel';

const CookieBanner = () => {
  const { pathname } = useLocation();
  const [choice, setChoice] = useState('loading');
  const [aggregateStatistics, setAggregateStatistics] = useState('on');

  useEffect(() => {
    setAggregateStatistics(getAggregateStatisticsPreference());
    if (isPrivateAnalyticsPath(pathname)) {
      disableOptionalTracking();
      return undefined;
    }

    if (isConsentUiHiddenPath(pathname)) {
      const storedChoice = getStoredConsent();
      setChoice(storedChoice);
      if (storedChoice === 'accepted') enableOptionalTracking();
      else disableOptionalTracking();
      return undefined;
    }

    ensureGoogleTag();
    const storedChoice = getStoredConsent();
    setChoice(storedChoice);
    if (storedChoice === 'accepted') enableOptionalTracking();
    else disableOptionalTracking();

    const openSettings = () => setChoice(null);
    window.addEventListener(CONSENT_SETTINGS_EVENT, openSettings);
    return () => window.removeEventListener(CONSENT_SETTINGS_EVENT, openSettings);
  }, [pathname]);

  if (isConsentUiHiddenPath(pathname) || choice === 'loading' || choice) return null;

  const accept = () => {
    storeConsent('accepted');
    setChoice('accepted');
    enableOptionalTracking();
  };

  const reject = () => {
    storeConsent('rejected');
    setChoice('rejected');
    disableOptionalTracking();
  };

  const updateAggregateStatistics = (preference) => {
    setAggregateStatisticsPreference(preference);
    setAggregateStatistics(preference);
  };

  return (
    <aside
      className="public-cookie-banner fixed bottom-2.5 left-2.5 z-[250] w-[calc(100%-1.25rem)] max-w-[288px] rounded-lg border border-[#b9d5e5] bg-white/95 p-2.5 text-left shadow-lg shadow-[#164a6c]/20 backdrop-blur-xl sm:bottom-4 sm:left-4 sm:max-w-[300px]"
      role="dialog"
      aria-label="Cookie and privacy choices"
      aria-live="polite"
    >
      <button
        type="button"
        onClick={reject}
        className="absolute left-1.5 top-1.5 flex h-[34.65px] w-[34.65px] items-center justify-center rounded-lg border border-[#c9dce8] bg-[#eef7fc] text-[#31536b] transition hover:border-[#49b9aa] hover:bg-[#e1f4f1] hover:text-[#0b3155] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7fd1]"
        aria-label="Close and reject optional cookies"
        title="Close and reject optional cookies"
      >
        <X className="h-5 w-5" aria-hidden="true" />
      </button>

      <div className="flex min-h-[35px] items-center justify-start gap-1.5 pl-10">
        <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-[#168879]" aria-hidden="true" />
        <h2 className="text-xs font-semibold text-[#0b3155]">Your privacy choices</h2>
      </div>
      <p className="mt-1 text-[10px] leading-[1.35] text-[#4d6c82]">
        Optional analytics and advertising cookies help us understand visits. No optional tracking data is stored or logged by PhantomPath unless you accept. Only your choice is saved on this device; essential security always works. Consent: UK GDPR Art. 6(1)(a), PECR Reg. 6.
      </p>
      <div className="mt-2 flex items-center justify-between gap-2 rounded-md border border-[#cfdfeb] bg-[#eef7fc] px-2 py-1.5">
        <span className="text-[9px] leading-tight text-[#4d6c82]">Anonymous first-party statistics</span>
        <div className="flex shrink-0 gap-1" role="group" aria-label="Anonymous first-party statistics preference">
          <button type="button" onClick={() => updateAggregateStatistics('on')} aria-pressed={aggregateStatistics === 'on'} className={`rounded px-1.5 py-1 text-[9px] ${aggregateStatistics === 'on' ? 'bg-[#49b9aa] text-[#082f2a]' : 'bg-[#dfeef6] text-[#58758a]'}`}>On</button>
          <button type="button" onClick={() => updateAggregateStatistics('off')} aria-pressed={aggregateStatistics === 'off'} className={`rounded px-1.5 py-1 text-[9px] ${aggregateStatistics === 'off' ? 'bg-[#0b3155] text-white' : 'bg-[#dfeef6] text-[#58758a]'}`}>Off</button>
        </div>
      </div>
      <div className="mt-2 flex flex-nowrap items-center gap-1">
        <button
          type="button"
          onClick={reject}
          className="whitespace-nowrap rounded-md border border-[#b9d5e5] bg-[#eef7fc] px-2.5 py-1.5 text-[9px] font-medium text-[#31536b] transition hover:border-[#0b7fd1] hover:bg-[#e3f2fb] hover:text-[#0b3155] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0b7fd1]"
        >
          Reject optional
        </button>
        <Link to="/cookie-policy" className="whitespace-nowrap px-1 py-1.5 text-[10px] font-semibold text-[#0b416d] underline-offset-4 hover:text-[#075f91] hover:underline">
          Cookie policy
        </Link>
        <button
          type="button"
          onClick={accept}
          className="whitespace-nowrap rounded-md bg-[#0b7fd1] px-2.5 py-1.5 text-[10px] font-bold text-white transition hover:bg-[#096aaf] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#49b9aa]"
        >
          Accept optional
        </button>
      </div>
    </aside>
  );
};

export default CookieBanner;
